import { FC } from 'react';
import { Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStore } from '~/stores';
import Auth from '~/scenes/Auth';
import Stream from '~/scenes/Stream';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: FC<ProtectedRouteProps> = observer(({ children }) => {
  const { authStore, schedulerStore } = useStore();

  // Check if tenant requires activation (default: false - open access)
  const requiresActivation = import.meta.env.VITE_REQUIRE_ACTIVATION === 'true';

  // Check if we have cached content available (offline-first)
  const hasCachedContent = schedulerStore.campaigns.length > 0;

  // Determine authorization based on tenant configuration
  const isAuthorized = requiresActivation
    ? // If activation is required: must be authenticated OR approved
      authStore.isAuthenticated || authStore.registrationStatus === 'approved'
    : // If activation is NOT required: always allow (open access)
      true;

  // ProtectedRoute roda a cada re-render — debug only
  console.debug('[ProtectedRoute] Checking authorization:', {
    requiresActivation,
    hasCachedContent,
    isAuthenticated: authStore.isAuthenticated,
    registrationStatus: authStore.registrationStatus,
    isAuthorized,
  });

  // Only block access if:
  // 1. Tenant requires activation AND
  // 2. Device is not authenticated/approved AND
  // 3. No cached content is available (to allow offline playback)
  if (!isAuthorized && !hasCachedContent) {
    // Manter warn — é evento real de redirect (mudança de rota)
    console.warn(
      '[ProtectedRoute] Not authorized and no cache, redirecting to /auth',
    );
    return <Navigate to="/auth" replace />;
  }

  console.debug('[ProtectedRoute] Authorized, rendering protected content');
  return <>{children}</>;
});

const Routes: FC = () => {
  return (
    <RouterRoutes>
      <Route path="/auth" element={<Auth />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Stream />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </RouterRoutes>
  );
};

export default Routes;
